import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router";
import { routes } from "@/app/routes";
import { ServicesProvider } from "@/app/services-provider";
import type { Services } from "@/lib/services";
import { ConsentBanner } from "@/lib/analytics/ConsentBanner";

/**
 * One QueryClient for the life of the tab.
 *
 * Readings are dated to the day they were computed, so nothing is refetched
 * merely because the window regained focus — a phone user flipping between
 * apps would otherwise see the Today screen flicker to a spinner every time
 * they came back. Staleness is per-query where it matters.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60_000,
    },
  },
});

const router = createBrowserRouter(routes);

interface AppProps {
  readonly services: Services;
}

export function App({ services }: AppProps) {
  return (
    <ServicesProvider services={services}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ConsentBanner analytics={services.analytics} />
      </QueryClientProvider>
    </ServicesProvider>
  );
}
